import { useQuery } from 'react-query';
import {
    TRBStatusTableRow,
    TRBMaxRiskTableRow,
    TRBFCProcessingTimes,
    TRBSnapshotUrls,
} from 'src/features/types/trbTypes';
const TRB_REFETCH_INTERVAL_MS = 300_000;

import { computeTimeToCPT } from 'src/features/utils/trbUtils';
import fetch from 'src/utils/APIGatewayClients';
import { getBackendAPIHostWithPrefix } from 'src/utils/Environment';

// API routes matching the Smithy model operations
const GET_TRB_STATUS_TABLE = '/getTRBStatusTable';
const GET_TRB_MAX_RISK_TABLE = '/getTRBMaxRiskTable';
const GET_TRB_FC_PROCESSING_TIMES = '/getTRBFCProcessingTimes';
const GET_TRB_SNAPSHOTS = '/getTRBSnapshots';

interface TRBStatusTableResponse {
    statusTableData: TRBStatusTableRow[];
}

interface TRBMaxRiskTableResponse {
    maxRiskTableData: TRBMaxRiskTableRow[];
}

interface TRBFCProcessingTimesResponse {
    fcProcessingTimes: TRBFCProcessingTimes;
}

interface TRBSnapshotsResponse {
    cptSnapshotUrl: string;
    r2fSnapshotUrl: string;
}

/**
 * Call the Newton backend API with an optional domain parameter.
 * Realm is determined server-side from the Lambda's deployment environment.
 */
async function trbBackendFetch<T>(path: string): Promise<T> {
    return fetch(
        `${getBackendAPIHostWithPrefix()}${path}`,
        {
            method: 'POST',
        },
    );
}

export function useTRBStatusTable() {
    return useQuery<TRBStatusTableRow[], Error>(
        ['trb-statusTable'],
        async () => {
            const response = await trbBackendFetch<TRBStatusTableResponse>(
                GET_TRB_STATUS_TABLE,
            );
            const rows = response.statusTableData ?? [];
            // Compute timeToCPT and timeToCPTSeconds client-side from cptUTC
            return rows.map((row) => {
                const { timeToCPT, timeToCPTSeconds } = computeTimeToCPT(row.cptUTC);
                return { ...row, timeToCPT, timeToCPTSeconds };
            });
        },
        {
            
            refetchInterval: TRB_REFETCH_INTERVAL_MS, // 5 minutes
            staleTime: 0,
            retry: 2,
        },
    );
}

export function useTRBMaxRiskTable() {
    return useQuery<TRBMaxRiskTableRow[], Error>(
        ['trb-maxRiskTable'],
        async () => {
            const response = await trbBackendFetch<TRBMaxRiskTableResponse>(
                GET_TRB_MAX_RISK_TABLE,
            );
            const rows = response.maxRiskTableData ?? [];
            // Compute timeToCPT and timeToCPTSeconds client-side from cptUTC
            return rows.map((row) => {
                const { timeToCPT, timeToCPTSeconds } = computeTimeToCPT(row.cptUTC);
                return { ...row, timeToCPT, timeToCPTSeconds };
            });
        },
        {
            
            refetchInterval: TRB_REFETCH_INTERVAL_MS,
            staleTime: 0,
            retry: 2,
        },
    );
}

export function useTRBFCProcessingTimes() {
    return useQuery<TRBFCProcessingTimes, Error>(
        ['trb-fcProcessingTimes'],
        async () => {
            const response = await trbBackendFetch<TRBFCProcessingTimesResponse>(
                GET_TRB_FC_PROCESSING_TIMES,
            );
            return response.fcProcessingTimes ?? {};
        },
        {
            
            refetchInterval: TRB_REFETCH_INTERVAL_MS,
            staleTime: 0,
            retry: 2,
        },
    );
}

export function useTRBSnapshots(enabled = true) {
    return useQuery<TRBSnapshotUrls, Error>(
        ['trb-snapshots'],
        async () => {
            const response = await trbBackendFetch<TRBSnapshotsResponse>(
                GET_TRB_SNAPSHOTS,
            );
            return {
                cptSnapshotUrl: response.cptSnapshotUrl ?? '',
                r2fSnapshotUrl: response.r2fSnapshotUrl ?? '',
            };
        },
        {
            enabled,
            refetchInterval: TRB_REFETCH_INTERVAL_MS,
            staleTime: 0,
            retry: 2,
        },
    );
}
